import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { memoGeneric } from '@/js/libs/memo/index.ts';

import { Button } from '../../Controls/Button/index.tsx';
import { ListItemDescription, ListItemLabel, ListItemWrapper } from './styled.ts';
import type { FormatOptionLabel, Option } from './types.ts';

type ListItemProps<T> = {
  dataTestId: string;
  /**
   * The width of the scrollbar of the container.
   * Used to calculate the list item’s right padding so that its action icon aligns with the bulk action icon.
   */
  scrollbarWidth: number;
  option: Option<T>;
  disabled?: boolean;
  formatOptionLabel?: FormatOptionLabel<T>;
  rowActionIcon?: ReactNode;
  onRowAction?: (option: Option<T>) => void;
};

export const ListItem = memoGeneric(function ListItem<T>({
  scrollbarWidth,
  option,
  onRowAction,
  disabled: propDisabled,
  formatOptionLabel,
  dataTestId,
  rowActionIcon,
}: ListItemProps<T>) {
  const disabled = propDisabled || option.disabled;
  const label = useMemo(() => formatOptionLabel?.(option) ?? option.label, [formatOptionLabel, option]);

  const handleActionClick = useCallbackRef(() => {
    onRowAction?.(option);
  });

  return (
    <ListItemWrapper disabled={disabled} data-testid={dataTestId} $scrollbarWidth={scrollbarWidth}>
      <ListItemLabel disabled={disabled}>
        {label}
        {option.description && <ListItemDescription>{option.description}</ListItemDescription>}
      </ListItemLabel>
      {rowActionIcon && (
        <Button onClick={handleActionClick} disabled={disabled} ghost dataTestId={`${dataTestId}-button`}>
          {rowActionIcon}
        </Button>
      )}
    </ListItemWrapper>
  );
});
